'use client'

import { ReactNode, useEffect, useState } from 'react'
import { Button, Icon, Text } from '@vinted/web-ui'
import { CheckmarkShield16, X12 } from '@vinted/monochrome-icons'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  getConversationPhishingPassiveReminder,
  updateConversationPhishingPassiveReminder,
  clickEvent,
} from '@marketplace-web/conversation-safety/conversation-safety-data'

import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'

type Props = {
  threadId: string
}

const PhishingReminderBanner = ({ threadId }: Props) => {
  const [isBannerDismissed, setIsBannerDismissed] = useState(false)
  const { track } = useTracking()

  const { fetch: fetchConversationPhishingPassiveReminder, data } = useFetch(
    getConversationPhishingPassiveReminder,
  )

  const translate = useTranslate('conversation.phishing_passive_reminder.banner')
  const translateA11y = useTranslate('common.a11y')

  useEffect(() => {
    fetchConversationPhishingPassiveReminder(threadId)
  }, [fetchConversationPhishingPassiveReminder, threadId])

  const handleBannerDismiss = async () => {
    track(
      clickEvent({
        target: 'phishing_reminder_dialog_close',
        targetDetails: JSON.stringify({
          thread_id: threadId,
        }),
      }),
    )

    setIsBannerDismissed(true)
    await updateConversationPhishingPassiveReminder(threadId, { hidden: true })
  }

  const handleLinkClick = () => {
    track(
      clickEvent({
        target: 'phishing_reminder_dialog_learn_more',
        targetDetails: JSON.stringify({
          thread_id: threadId,
        }),
      }),
    )
  }

  if (!data || data.hidden || isBannerDismissed) return null

  return (
    <div className="phishing-reminder-banner">
      <div className="phishing-reminder-banner__container">
        <div className="phishing-reminder-banner__prefix">
          <Icon
            color="primary-default"
            name={CheckmarkShield16}
            aria={{
              'aria-hidden': 'true',
            }}
            display="block"
          />
        </div>
        <div className="phishing-reminder-banner__content">
          <Text
            as="p"
            type="caption"
            text={translate('body', {
              'more-safety-tips-link': (chunks: Array<ReactNode>) => (
                <HelpCenterFaqEntryUrl
                  key="more-safety-tips-link"
                  type={FaqEntryType.SpamPhishing}
                  render={faqUrl => (
                    <a href={faqUrl} target="_blank" rel="noreferrer" onClick={handleLinkClick}>
                      {chunks}
                    </a>
                  )}
                />
              ),
            })}
          />
        </div>
        <div className="phishing-reminder-banner__dismiss-button">
          <Button
            size="small"
            styling="flat"
            iconName={X12}
            theme="amplified"
            onClick={handleBannerDismiss}
            aria={{ 'aria-label': translateA11y('actions.dialog_close') }}
            testId="phishing-reminder-banner-close-button"
            inline
          />
        </div>
      </div>
    </div>
  )
}

export default PhishingReminderBanner
